import { z } from 'zod';
import { IdSchema, InstantSchema } from './common.ts';

/**
 * A per-record change log (P5-14).
 *
 * SPEC §9 criterion 5 ends "the change log records the approval", and until
 * now no module had one. `docs/features/08-purchase-orders-bills.md` §5 asks
 * for one on every purchase order and bill edit, and the frames put a
 * `View Change Log` button on both.
 *
 * ## Why this is not the audit log the maintainer declined
 *
 * `docs/features/02-schedule.md` §4.1 records a decision from 2026-10-12
 * **not** to build a job audit log, and the reason was specific: a schedule
 * cascade already writes exactly 500 documents, `MAX_BATCH_WRITES` is 500,
 * and one audit entry per moved item would double it and fail hard. That
 * reasoning is about cascades and it still holds — **the schedule is
 * deliberately not logged here either**.
 *
 * A change order approval is a different shape: one document write, one
 * entry. There is no batch to overflow. The other objection recorded there
 * was that a trigger "no in-memory adapter can reproduce" would break the
 * architecture rule; that has since been overtaken by events — this repo
 * now runs several triggers, tests them against the real runtime in
 * `*.triggers.test.ts`, and reproduces their effects inline for the dev
 * server where a screen depends on them.
 *
 * ## Shape
 *
 * A **subcollection of the record**, not a job-wide collection with a
 * `recordId` field. Three reasons, and the third is the one that decided
 * it:
 *
 * - a record's log is then an unfiltered query, so it needs no composite
 *   index — and on 2026-10-13 the dev project turned out to be unable to
 *   create one (SCRUM-200), which makes an index-free design worth
 *   something concrete;
 * - the rules can gate it per record kind, which is what "who may see the
 *   log of a bill" needs;
 * - deleting a record takes its log with it, which is right: a log is about
 *   a thing, and a log with no thing is noise nobody can act on.
 */
export const ChangeLogChangeSchema = z.object({
  /** The field as the product names it, not as the document spells it. */
  field: z.string().min(1).max(60),
  /** Rendered values, because a log is read rather than computed over. */
  from: z.string().max(200).optional(),
  to: z.string().max(200).optional(),
});
export type ChangeLogChange = z.infer<typeof ChangeLogChangeSchema>;

export const ChangeLogEntrySchema = z.object({
  id: IdSchema,
  at: InstantSchema,
  /** Who did it. `'system'` for a scheduled function, as elsewhere. */
  byUid: z.string().min(1),
  /** One line, already written: "Released to the owner." */
  summary: z.string().min(1).max(300),
  changes: z.array(ChangeLogChangeSchema).default([]),
});
export type ChangeLogEntry = z.infer<typeof ChangeLogEntrySchema>;

/**
 * The entry id for a write, derived from **when** it happened.
 *
 * Firestore triggers are at-least-once: the same write can be delivered
 * twice, and a log that appended on each delivery would show an approval
 * happening twice. A deterministic id makes a redelivery overwrite its own
 * entry instead.
 *
 * Two writes to one record inside the same millisecond would share an id
 * and so merge into one entry. That is the safer of the two failures — a
 * merged entry is a log that under-reports a change nobody made
 * separately, where a duplicate is a log that invents one — and it needs
 * two people editing the same change order in the same millisecond.
 */
export function changeLogEntryId(at: string): string {
  return at.replace(/[^0-9]/g, '');
}

/**
 * What changed between two versions of a record, for a log.
 *
 * Takes a map of field name to a renderer, so the log says "Price" and
 * "$3,600.00" rather than `coTotals.price` and `{amount: 360000}`. A field
 * whose rendered value is unchanged is not a change, which is what makes
 * this safe to run on every write: most writes touch `updatedAt` and
 * nothing a person would recognise, and those produce no entry at all.
 */
export function diffForLog<T>(
  before: T | undefined,
  after: T,
  fields: readonly { readonly field: string; readonly render: (record: T) => string | undefined }[],
): ChangeLogChange[] {
  const changes: ChangeLogChange[] = [];
  for (const { field, render } of fields) {
    const to = render(after);
    const from = before === undefined ? undefined : render(before);
    if (from === to) continue;
    changes.push({
      field,
      ...(from === undefined ? {} : { from }),
      ...(to === undefined ? {} : { to }),
    });
  }
  return changes;
}

/**
 * One line describing a set of changes.
 *
 * Written for somebody scanning a list, so it names what changed rather
 * than reciting values: "Status and price changed" reads better in a column
 * than a paragraph, and the values are in the entry beside it.
 *
 * A **creation** — nothing before — says so, because "Status changed from
 * nothing to draft" is a sentence nobody wants to read.
 */
export function summariseChanges(changes: readonly ChangeLogChange[], created: boolean): string {
  if (created) return 'Created';
  if (changes.length === 0) return 'Edited';
  const names = changes.map((change) => change.field);
  if (names.length === 1) return `${names[0]} changed`;
  const last = names[names.length - 1]!;
  return `${names.slice(0, -1).join(', ')} and ${last} changed`;
}
